import classNames from 'classnames';

import { Box } from '../Box';
import { BorderRadius } from '../Theme';

import {
  CounterBackgroundColor,
  CounterColor,
  CounterSize,
} from './Counter.types';
import { useStyles } from './Counter.styles';

/**
 * Свойства компонента {@link Counter}
 */
export type CounterProps = {
  /**
   * Фоновый цвет счетчика.
   *
   * @defaultValue `'white'`
   */
  backgroundColor?: keyof typeof CounterBackgroundColor;

  /**
   * Дополнительное имя CSS-класса для применения к счетчику.
   */
  className?: string;

  /**
   * Цвет текста счетчика.
   */
  color?: keyof typeof CounterColor;

  /**
   * Значение счетчика для отображения.
   */
  counter: number | string;

  /**
   * Флаг для отображения знака '+' для положительных чисел.
   *
   * @defaultValue `false`
   */
  signed?: boolean;

  /**
   * Флаг для отображения знака '+' для указания, что значение счетчика превышает максимальное.
   *
   * @defaultValue `false`
   */
  moreThanMax?: boolean;

  /**
   * Размер счетчика.
   *
   * @defaultValue `'medium'`
   */
  size?: keyof typeof CounterSize;

  /**
   * Значение для определения скругления фонового цвета.
   *
   * @defaultValue `'s'`
   */
  borderRadius?: keyof BorderRadius;

  /**
   * Тестовый идентификатор для счетчика.
   *
   * @defaultValue `'counter'`
   */
  testId?: string;
};

/**
 * Компонент, который отображает счетчик с возможностью стилизации и добавления знаков.
 *
 * @public
 * @param backgroundColor - Фоновый цвет счетчика.
 * @param className - Дополнительное имя CSS-класса для применения к счетчику.
 * @param color - Цвет текста счетчика.
 * @param counter - Значение счетчика для отображения.
 * @param signed - Флаг для отображения знака '+' для положительных чисел.
 * @param moreThanMax - Флаг для отображения знака '+' для указания, что значение счетчика превышает максимальное.
 * @param size - Размер счетчика.
 * @param borderRadius - Значение для определения скругления фонового цвета.
 * @param testId - Тестовый идентификатор для счетчика.
 * @returns Компонент Counter.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link CounterProps}
 */
export const Counter = ({
  backgroundColor = 'white',
  className,
  color,
  counter,
  signed = false,
  size = 'medium',
  moreThanMax = false,
  borderRadius = 's',
  testId = 'counter',
}: CounterProps) => {
  const { classes } = useStyles({
    backgroundColor: CounterBackgroundColor[backgroundColor],
    color: color,
    size: CounterSize[size],
    signed,
    borderRadius,
  });

  const sign = signed && Number(counter) > 0 ? '+' : '';

  return (
    <Box testId={testId} className={classNames(classes.counter, className)}>
      {sign}
      {counter}
      {moreThanMax ? '+' : ''}
    </Box>
  );
};
